import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import FontFamily from '../../../constants/fonts';
import LHModalPop from '../../../components/LHModalPop';
import {
  BackIcon,
  SaveIcon,
  ForwardIcon,
  SubmitIcon,
  CompleteIcon,
} from './StandupIcons';

interface StandUpBottomActionsProps {
  activeTab: string;
  onBackPress: () => void;
  backToEditPress: () => void;
  onSavePress: () => void;
  onContinuePress: () => void;
  onCompletePress?: () => void;
  onSubmitPress?: () => void;
  isEditMode?: boolean;
  isViewMode?: boolean;
  favoriteSparcCards: string[];
  favoriteLighthouseCards: string[];
  favoriteLighthouseTasks: string[];
  standupItemStatus?: string;
  isSubmitted?: boolean;
  isBackToEditInViewMode?: boolean;
  selectedKpiCards?: any[]; // <-- added prop
}

export const StandUpBottomActions: React.FC<StandUpBottomActionsProps> = ({
  activeTab,
  onBackPress,
  backToEditPress,
  onSavePress,
  onContinuePress,
  onSubmitPress,
  onCompletePress,
  isEditMode = false,
  isViewMode = false,
  isSubmitted = false,
  isBackToEditInViewMode = false,
  favoriteSparcCards,
  favoriteLighthouseCards,
  favoriteLighthouseTasks,
  standupItemStatus,
  selectedKpiCards = [], // default to empty array
}) => {
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [isSaveButtonHovered, setIsSaveButtonHovered] = useState(false);
  const [isContinueButtonHovered, setIsContinueButtonHovered] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Handle showing completion confirmation modal (only in view mode)
  const handleShowCompletionModal = () => {
    if (isViewMode || isSubmitted) {
      // View mode: show confirmation modal
      setShowCompletionModal(true);
    } else {
      // Create/Edit mode: submit directly without modal
      if (onCompletePress) {
        onCompletePress();
      }
    }
  };

  // Handle completion confirmation
  const handleConfirmCompletion = () => {
    setShowCompletionModal(false);
    if (onCompletePress) {
      onCompletePress();
    }
  };

  const handleSubmitCompletion = () => {
     if (isViewMode) {
      // View mode: show confirmation modal
      setShowCompletionModal(true);
    } else {
      // Create/Edit mode: submit directly without modal
      if (onSubmitPress) {
        onSubmitPress();
      }
    }
  }

  // Handle completion cancellation
  const handleCancelCompletion = () => {
    setShowCompletionModal(false);
  };

  const backToEditPressHandle = async () => {
    if (backToEditPress) {
      backToEditPress();
    }
  }

  // Calculate if Continue button should be enabled based on tab and favorites
  const isContinueEnabled = () => {
    switch (activeTab) {
      case 'review-performance':
        return (selectedKpiCards && selectedKpiCards.length > 0);
      case 'key-activities':
        return true;
      case 'news':
        // Always enable for NewsTab
        return true;
      case 'review-progress':
        // Always enable for ReviewProgressTab (Submit button)
        return true;
      default:
        return true;
    }
  };

  const continueEnabled = isContinueEnabled();

  // Get tooltip text based on active tab
  const getTooltipText = () => {
    switch (activeTab) {
      case 'review-performance':
        return 'Select KPIs to continue';
      case 'key-activities':
        return 'Select tasks to continue';
      default:
        return '';
    }
  };

  // Hide all bottom buttons if we're in view mode and standup is completed
  if ((isViewMode || isSubmitted) && standupItemStatus?.toLowerCase() === 'completed') {
    return null;
  }

  return (
    <>
    <View style={styles.bottomActionsContainer}>
      <View style={styles.bottomButtons}>
        {(isViewMode || isSubmitted) && !isBackToEditInViewMode ? (
          // View mode: only show Back to Edit and Submit buttons
          <>
            {/* Back to Edit Button - hidden for Incomplete status */}
            {standupItemStatus?.toLowerCase() !== 'incomplete' && (
              <Pressable
                style={({ pressed }) => [
                  styles.backToEditButton,
                  isBackButtonHovered && styles.backToEditButton,
                  pressed && styles.backToEditButton,
                ]}
                onPress={backToEditPressHandle}
                onHoverIn={() => setIsBackButtonHovered(true)}
                onHoverOut={() => setIsBackButtonHovered(false)}
              >
                <BackIcon color={isBackButtonHovered ? "#004895" : "#004895"} />
                <Text style={[styles.backToEditButtonText, isBackButtonHovered && styles.backToEditButtonText]}>
                  Back to Edit
                </Text>
              </Pressable>
            )}
    
            {/* Submit Button */}
             {standupItemStatus?.toLowerCase() !== 'incomplete' && ( <Pressable
              style={({ pressed }) => [
                styles.continueButton,
                isContinueButtonHovered && styles.continueButtonHovered,
                pressed && styles.buttonPressed,
              ]}
              onPress={handleShowCompletionModal}
              onHoverIn={() => setIsContinueButtonHovered(true)}
              onHoverOut={() => setIsContinueButtonHovered(false)}
            >
              <Text style={[styles.continueButtonText, isContinueButtonHovered && styles.continueButtonTextHovered]}>
                Complete
              </Text>
              <CompleteIcon />
            </Pressable>)}
          </>
        ) : (
          // Normal mode: show all buttons
          <>
            {/* Back Button */}
            <Pressable
              style={({ pressed }) => [
                styles.backButton,
                isBackButtonHovered && styles.buttonHovered,
                pressed && styles.buttonPressed,
              ]}
              onPress={onBackPress}
              onHoverIn={() => setIsBackButtonHovered(true)}
              onHoverOut={() => setIsBackButtonHovered(false)}
            >
              <BackIcon color={isBackButtonHovered ? "#fff" : "#004895"} />
              <Text style={[styles.backButtonText, isBackButtonHovered && styles.buttonTextHovered]}>
                Back
              </Text>
            </Pressable>

            {/* Save for Later Button */}
            <Pressable
              style={({ pressed }) => [
                styles.saveButton,
                isSaveButtonHovered && styles.buttonHovered,
                pressed && styles.buttonPressed,
              ]}
              onPress={onSavePress}
              onHoverIn={() => setIsSaveButtonHovered(true)}
              onHoverOut={() => setIsSaveButtonHovered(false)}
            >
              <SaveIcon color={isSaveButtonHovered ? "#fff" : "#004895"} />
              <Text style={[styles.saveButtonText, isSaveButtonHovered && styles.buttonTextHovered]}>
                Save for Later
              </Text>
            </Pressable>

            {/* Continue/Submit Button */}
            {activeTab === "review-progress" ? (
              <Pressable
                style={({ pressed }) => [
                  styles.continueButton,
                  isContinueButtonHovered && styles.continueButtonHovered,
                  pressed && styles.buttonPressed,
                ]}
                onPress={handleSubmitCompletion}
                onHoverIn={() => setIsContinueButtonHovered(true)}
                onHoverOut={() => setIsContinueButtonHovered(false)}
              >
                <SubmitIcon />
                <Text style={[styles.continueButtonText, isContinueButtonHovered && styles.continueButtonTextHovered]}>
                Submit
                </Text>
              </Pressable>
            ) : (
              <View style={styles.continueButtonWrapper}>
                <Pressable
                  style={({ pressed }) => [
                    styles.continueButton,
                    !continueEnabled && styles.continueButtonDisabled,
                    continueEnabled && isContinueButtonHovered && styles.continueButtonHovered,
                    pressed && continueEnabled && styles.buttonPressed,
                  ]}
                  onPress={continueEnabled ? onContinuePress : undefined}
                  disabled={!continueEnabled}
                  onHoverIn={() => continueEnabled && setIsContinueButtonHovered(true)}
                  onHoverOut={() => continueEnabled && setIsContinueButtonHovered(false)}
                  {...(Platform.OS === 'web' && !continueEnabled
                    ? {
                        onMouseEnter: () => setShowTooltip(true),
                        onMouseLeave: () => setShowTooltip(false),
                      }
                    : {})}
                >
                  <Text style={[
                    styles.continueButtonText,
                    !continueEnabled && styles.continueButtonTextDisabled,
                    continueEnabled && isContinueButtonHovered && styles.continueButtonTextHovered
                  ]}>
                    Continue
                  </Text>
                  <ForwardIcon color={!continueEnabled ? "#9E9E9E" : (continueEnabled && isContinueButtonHovered ? "#fff" : "#ABABAB")} />
                </Pressable>
                {showTooltip && !continueEnabled && Platform.OS === 'web' && (
                  <View style={styles.tooltip}>
                    <Text style={styles.tooltipText}>{getTooltipText()}</Text>
                  </View>
                )}
              </View>
            )}
          </>
        )}
      </View>
    </View>
    
    {/* Completion Confirmation Modal */}
    <LHModalPop
      visible={showCompletionModal}
      onClose={handleCancelCompletion}
      title="Confirm Submission"
      description="Once Completed, a stand up cannot be modified. Are you sure you would like to continue?"
      showFooter={true}
      primaryButtonText="Complete"
      secondaryButtonText="Cancel"
      onPrimaryPress={handleConfirmCompletion}
      onSecondaryPress={handleCancelCompletion}
      showCloseButton={true}
    />
  </>
  );
};

const styles = StyleSheet.create({
  bottomActionsContainer: {
    marginTop: 24,
    marginBottom: 20,
    alignItems: 'flex-end',
  },
  bottomButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#004895',
    minWidth: 88,
    height: 40,
    justifyContent: 'center',
  },
  backToEditButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    backgroundColor: 'transparent',
    gap: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#004895',
    fontFamily: FontFamily.bold,
  },
  backToEditButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#004895',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap:8,
    paddingVertical: 8,
    paddingHorizontal: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#004895',
    minWidth: 150,
    height: 40,
    justifyContent: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#004895',
    fontFamily: FontFamily.bold,
  },
  continueButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: '#004895',
    borderWidth: 0,
    gap: 8,
  },
  continueButtonText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  continueButtonDisabled: {
    backgroundColor: '#D9D9D9',
    borderColor: '#D9D9D9',
    opacity: 0.6,
  },
  continueButtonTextDisabled: {
    color: '#ADADAD',
  },
  continueButtonHovered: {
    backgroundColor: '#003366',
  },
  continueButtonTextHovered: {
    color: '#FFFFFF',
  },
  buttonHovered: {
    backgroundColor: '#004895',
  },
  buttonTextHovered: {
    color: '#fff',
  },
  buttonPressed: {
    opacity: 0.8,
  },
  continueButtonWrapper: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: [{ translateX: '-50%' }],
    marginBottom: 8,
    backgroundColor: '#1C1C1C',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 150,
    ...(Platform.OS === 'web' && {
      // @ts-ignore - web-only styles
      transform: 'translateX(-50%)',
    }),
  },
  tooltipText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    textAlign: 'center',
  },
});
